import {Button, Table, Tag} from 'antd';
import {useState} from 'react';

import {EditEventFormModal} from '../../../../component/events/event-form/edit-event-form-modal';
import {EventPreview} from '../../../../component/events/event-preview/event-preview';
import {getEventCatalogStatusConfig, getEventStatusConfig} from '../../../../component/events/events-helper';
import {PublicationPreviewThumb} from '../../../../component/posts/publication-preview-thumb/publication-preview-thumb';
import {SelectedCompaniesModal} from '../../../../component/posts/selected-companies/selected-companies-modal';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useEvents, useEventSelectedCompanies} from '../../../../service/events/events-hook';
import {EventListItemType, EventSelectedCompanyType} from '../../../../service/events/events-type';
import {PublicationListFilterType} from '../../../../service/publication/publication-filter-type';
import {useFormat} from '../../../../util/format-hook/format-hook';

import {EventActions} from './event-actions';
import {eventsTableColumnList} from './events-table-const';
import {EventsTableRenderingType} from './events-table-type';

type PropsType = {
    filter?: PublicationListFilterType;
};

export function EventsTable({filter}: PropsType): JSX.Element {
    const {data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage} = useEvents(filter, {
        staleTime: Number.POSITIVE_INFINITY,
    });
    const {getFormattedDateTime} = useFormat();
    const {getLocalizedString} = useLocale();

    const [activeEventId, setActiveEventId] = useState<number | null>(null);
    const [editingEventId, setEditingEventId] = useState<number | null>(null);

    const eventsData = data?.pages.flatMap((page) => page.results) ?? [];

    function renderEventRow(event: EventListItemType): EventsTableRenderingType {
        const statusConfig = getEventStatusConfig(event.status);

        return {
            key: String(event.id),
            title: event.title,
            preview: event.preview?.url ? (
                <PublicationPreviewThumb onClick={() => setActiveEventId(event.id)} src={event.preview.url} />
            ) : (
                '—'
            ),
            status: (
                <Tag color={statusConfig.tagColor}>
                    <Locale stringKey={statusConfig.langKey} />
                </Tag>
            ),
            startAt: event.startAt ? getFormattedDateTime(Date.parse(event.startAt)) : '—',
            finishAt: event.finishAt ? getFormattedDateTime(Date.parse(event.finishAt)) : '—',
            companiesCount: (
                <SelectedCompaniesModal<EventSelectedCompanyType>
                    companiesCount={event.companiesCount}
                    objectId={event.id}
                    renderStatus={(record) => {
                        const catalogStatusConfig = getEventCatalogStatusConfig(record.status);

                        return (
                            <Tag color={catalogStatusConfig.tagColor}>
                                <Locale stringKey={catalogStatusConfig.langKey} />
                            </Tag>
                        );
                    }}
                    subtitle={event.title}
                    title={getLocalizedString('EVENTS__SELECTED_COMPANIES__TITLE')}
                    useCompanies={useEventSelectedCompanies}
                />
            ),
            date: event.createdAt ? getFormattedDateTime(Date.parse(event.createdAt)) : '—',
            actions: <EventActions event={event} onEdit={setEditingEventId} />,
        };
    }

    return (
        <>
            <Table<EventsTableRenderingType>
                columns={eventsTableColumnList}
                dataSource={eventsData.map(renderEventRow)}
                loading={isLoading}
                pagination={false}
                scroll={{x: true}}
                size="middle"
            />

            {hasNextPage && (
                <div style={{marginTop: 16, textAlign: 'center'}}>
                    <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                        <Locale stringKey="POSTS__STORIES_TABLE__LOAD_MORE" />
                    </Button>
                </div>
            )}

            <EventPreview activeEventId={activeEventId} onClose={() => setActiveEventId(null)} />

            {editingEventId !== null && (
                <EditEventFormModal
                    eventId={editingEventId}
                    setIsOpen={(isOpen) => {
                        if (!isOpen) {
                            setEditingEventId(null);
                        }
                    }}
                />
            )}
        </>
    );
}
